import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronDown, Star } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { type MenuItem, type PhaseCode, isItemStarred, isItemVisible } from './menu.config';

interface NavItemProps {
  item: MenuItem;
  userPhaseOrder: number;
  selectedPhase: PhaseCode | null;
  depth?: number;
  /** Compteurs optionnels (pastilles) : clé = `MenuItem.badgeKey`. */
  badgeCounts?: Readonly<Record<string, number>>;
}

/**
 * Rendu récursif d'un item de menu :
 * - Filtre l'item s'il n'est pas visible pour la phase utilisateur.
 * - Choisit entre `<Link>` (item feuille) et dropdown (item parent).
 * - Affiche l'étoile selon `isItemStarred` (parent = OR des enfants visibles).
 */
export function NavItem({
  item,
  userPhaseOrder,
  selectedPhase,
  depth = 0,
  badgeCounts,
}: NavItemProps) {
  if (!isItemVisible(item, userPhaseOrder)) return null;

  const starred = isItemStarred(item, selectedPhase, userPhaseOrder);
  const hasChildren = item.children && item.children.length > 0;

  if (hasChildren) {
    return (
      <DropdownNavItem
        item={item}
        userPhaseOrder={userPhaseOrder}
        selectedPhase={selectedPhase}
        starred={starred}
        depth={depth}
        badgeCounts={badgeCounts}
      />
    );
  }

  return <LinkNavItem item={item} starred={starred} depth={depth} badgeCounts={badgeCounts} />;
}

function MenuCountBadge({
  count,
  variant = 'amber',
}: {
  count: number;
  variant?: 'amber' | 'rose';
}) {
  if (count <= 0) return null;
  const label = count > 99 ? '99+' : String(count);
  const bg = variant === 'rose' ? 'bg-rose-600' : 'bg-amber-500';
  return (
    <span
      className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none text-white ${bg}`}
      aria-label={`${count} éléments`}
    >
      {label}
    </span>
  );
}

function sumChildBadgeCounts(
  item: MenuItem,
  userPhaseOrder: number,
  badgeCounts: Readonly<Record<string, number>> | undefined,
): number {
  if (!item.children?.length || !badgeCounts) return 0;
  return item.children.reduce((sum, child) => {
    if (!isItemVisible(child, userPhaseOrder)) return sum;
    const k = child.badgeKey;
    if (!k) return sum;
    return sum + (badgeCounts[k] ?? 0);
  }, 0);
}

interface LinkNavItemProps {
  item: MenuItem;
  starred: boolean;
  depth: number;
  badgeCounts?: Readonly<Record<string, number>>;
}

function LinkNavItem({ item, starred, depth, badgeCounts }: LinkNavItemProps) {
  const Icon = item.icon;
  const isChild = depth > 0;
  const n = item.badgeKey ? (badgeCounts?.[item.badgeKey] ?? 0) : 0;
  const badgeVariant = item.badgeKey === 'publication-rejected' ? 'rose' : 'amber';

  const baseClasses = isChild
    ? 'flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900'
    : 'flex items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900';

  return (
    <Link
      to={item.to ?? '/'}
      className={baseClasses}
      activeProps={{ className: '!bg-emerald-50 !text-emerald-700' }}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {Icon && <Icon className="h-5 w-5 shrink-0 opacity-75" />}
        <span className="truncate">{item.label}</span>
        <MenuCountBadge count={n} variant={badgeVariant} />
      </div>
      {starred && <Star className="h-3.5 w-3.5 shrink-0 fill-emerald-500 text-emerald-500" />}
    </Link>
  );
}

interface DropdownNavItemProps {
  item: MenuItem;
  userPhaseOrder: number;
  selectedPhase: PhaseCode | null;
  starred: boolean;
  depth: number;
  badgeCounts?: Readonly<Record<string, number>>;
}

function DropdownNavItem({
  item,
  userPhaseOrder,
  selectedPhase,
  starred,
  depth,
  badgeCounts,
}: DropdownNavItemProps) {
  const Icon = item.icon;
  const ref = useRef<HTMLDivElement>(null);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const groupBadge = sumChildBadgeCounts(item, userPhaseOrder, badgeCounts);

  /** Le parent est "actif" si la route courante matche son `to` ou celui d'un de ses enfants. */
  const allPaths = [item.to, ...(item.children?.map((c) => c.to) ?? [])].filter((p): p is string =>
    Boolean(p),
  );
  const isActive = allPaths.some((p) => pathname === p || pathname.startsWith(`${p}/`));

  const [open, setOpen] = useState(isActive);

  useEffect(() => {
    if (isActive) setOpen(true);
  }, [isActive]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const headerClasses = `flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
    isActive
      ? 'bg-emerald-50 text-emerald-700'
      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
  }`;

  const header = (
    <>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {Icon && (
          <Icon
            className={`h-5 w-5 shrink-0 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`}
          />
        )}
        <span className="truncate">{item.label}</span>
        <MenuCountBadge count={groupBadge} />
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {starred && <Star className="h-3.5 w-3.5 fill-emerald-500 text-emerald-500" />}
        <ChevronDown
          className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </div>
    </>
  );

  return (
    <div ref={ref} className="relative w-full">
      {item.to ? (
        <Link
          to={item.to}
          className={headerClasses}
          activeProps={{ className: '!bg-emerald-50 !text-emerald-700' }}
          onClick={() => setOpen((v) => !v)}
        >
          {header}
        </Link>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={headerClasses}
          aria-expanded={open}
        >
          {header}
        </button>
      )}

      {open && (
        <div className="mt-1 flex flex-col gap-1 pl-11 pr-3">
          {item.children?.map((child) => (
            <NavItem
              key={child.id}
              item={child}
              userPhaseOrder={userPhaseOrder}
              selectedPhase={selectedPhase}
              depth={depth + 1}
              badgeCounts={badgeCounts}
            />
          ))}
        </div>
      )}
    </div>
  );
}
